import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"npm_package_dependencies_vue2_gmap_custom_marker":"^5.6.2","npm_package_dependencies_lockfile":"^1.0.4","npm_package_scripts_nuxt_build":"yarn run isd:static-files && nuxt build","npm_package_devDependencies_webpack_cli":"^4.10.0","npm_package_dependencies_vue_geolocation_api":"^0.1.21","npm_package_scripts_isd_static_files_custom_icon_definitions":"yarn run isd:static-files:create:custom-icon-definitions && yarn run isd:static-files:copy:custom-icon-definitions","npm_config_version_commit_hooks":"true","npm_config_user_agent":"yarn/1.22.19 npm/? node/v14.21.3 linux x64","NODE_VERSION":"14.21.3","npm_package_dependencies__p2media_new_create_font_awesome_icon_definitions":"^1.1.2","npm_package_scripts_isd_static_files_copy_rte_css":"node util/is-local.js && yarn run isd:static-files:copy:local:rte.css || yarn run isd:static-files:copy:docker:rte.css","npm_package_bugs_url":"https://git.p2media.de/p2media/internal/frontend/p2media-webpack-core/-/issues","npm_config_bin_links":"true","YARN_VERSION":"1.22.19","npm_node_execpath":"/usr/local/bin/node","npm_package_dependencies_vue_toastification":"^1.7.11","npm_package_dependencies_postinstall_postinstall":"^2.1.0","npm_package_scripts_nuxt_generate":"nuxt generate","npm_config_init_version":"1.0.0","SHLVL":"4","npm_package_dependencies_vue_scrollto":"^2.18.2","HOME":"/root","npm_package_devDependencies_sass":"1.32.8","npm_package_dependencies_vue_prlx":"^1.3.4","npm_package_dependencies_vue_multiselect":"^2.1.6","npm_package_dependencies__labor_digital_helferlein":"^4.2.0","npm_package_dependencies__fortawesome_pro_light_svg_icons":"^5.14.0","npm_package_dependencies_nuxt_cookie_control":"^1.9.93","npm_package_dependencies_azure_search":"^0.0.21","npm_package_dependencies__braid_vue_formulate_i18n":"^1.13.0","npm_config_init_license":"MIT","YARN_WRAP_OUTPUT":"false","npm_package_dependencies__nuxtjs_sitemap":"^2.4.0","npm_config_version_tag_prefix":"v","npm_package_dependencies_glightbox":"^3.0.5","npm_package_dependencies__nuxtjs_pwa":"^3.3.5","npm_package_dependencies__nuxtjs_gtm":"^2.4.0","npm_package_scripts_postinstall":"patch-package","npm_package_scripts_isd_static_files_rte_css":"yarn run isd:static-files:create:rte.css && yarn run isd:static-files:copy:rte.css","npm_package_contributors_0_email":"moritz.ngo@p2media.de","npm_package_dependencies_vue_countup_v2":"^4.0.0","npm_package_dependencies_qs":"^6.10.1","npm_package_dependencies__fortawesome_free_solid_svg_icons":"^5.15.1","npm_package_contributors_1_email":"casian.blanaru@p2media.de","npm_package_description":"Optimale Pflege und Werterhalt Ihrer Immobilien und Grundstücke.","npm_package_dependencies_vue_awesome_swiper":"^4.1.1","npm_package_dependencies_nuxt":"2.15.8","npm_package_homepage":"https://git.p2media.de/p2media/internal/frontend/p2media-webpack-core","npm_package_devDependencies_prettier":"^2.1.1","npm_package_dependencies_vue_fullpage_js":"^0.1.6","npm_package_scripts_nodemodulecache":"rm -rf node_modules/.cache","npm_package_dependencies_vue_social_sharing":"^3.0.3","npm_package_private":"true","npm_package_dependencies_nuxt_jsonld":"^1.5.2","npm_config_registry":"https://registry.yarnpkg.com","npm_package_dependencies_vue_in_viewport_directive":"^2.0.2","npm_package_dependencies_nuxt_vue_multiselect":"^1.0.4","npm_package_dependencies_vue_observe_visibility":"^0.4.6","npm_package_scripts_isd_static_files_copy_docker_custom_icon_definitions":"mkdir -p ./dist/isd-sitepackage/Resources/Public/Icons/CustomIcons && \\cp assets/custom-icons/*.svg ./dist/isd-sitepackage/Resources/Public/Icons/CustomIcons","npm_package_scripts_isd_static_files":"yarn run isd:static-files:rte.css && yarn run isd:static-files:custom-icon-definitions","npm_config_ignore_scripts":"","npm_config_version":"1.22.19","npm_package_dependencies_axios":"^0.21.1","PATH":"/tmp/yarn--1757337795341-0.7239878104694715:/usr/src/app/node_modules/.bin:/usr/local/share/.config/yarn/link/node_modules/.bin:/usr/local/libexec/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/bin/node_modules/npm/bin/node-gyp-bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin","NODE":"/usr/local/bin/node","npm_package_dependencies_vue_google_oauth2":"^1.5.8","npm_package_dependencies_swiper":"^5.4.1","npm_package_resolutions_sharp":"0.30.7","npm_package_repository_type":"git","npm_package_name":"@p2media/webpack-core","npm_package_dependencies_vue_autosuggest":"^2.2.0","npm_package_dependencies_mini_css_extract_plugin":"^0.12.0","npm_package_dependencies__braid_vue_formulate":"^2.5.2","npm_package_scripts_isd_static_files_create_custom_icon_definitions":"node node_modules/@p2media/new-create-font-awesome-icon-definitions/index.js --input-files=./assets/custom-icons/*.svg --output-directory=./assets/custom-icon-definitions --prefix=isd","npm_config__p2media_registry":"https://npm.p2media.de/","npm_package_dependencies_countup_js":"^2.0.7","npm_package_dependencies__fortawesome_vue_fontawesome":"^0.1.10","npm_lifecycle_script":"yarn run isd:static-files && nuxt build","npm_package_dependencies_vue_media":"^1.1.1","npm_package_dependencies_marker_clusterer_plus":"^2.1.4","npm_package_dependencies__fortawesome_fontawesome_pro":"^5.14.0","npm_package_contributors_0_name":"Moritz Ngo","npm_package_browserslist_0":"> 1%","npm_package_dependencies_vue2_google_maps":"^0.10.7","npm_package_dependencies_vue_typeahead_bootstrap":"^2.12.0","npm_package_dependencies_vue_foldable":"^1.0.0","npm_package_dependencies__nuxt_typescript_build":"^2.1.0","npm_package_dependencies__azure_service_bus":"^7.3.0","npm_package_scripts_nuxt_dev":"node --max-old-space-size=8192 node_modules/nuxt/bin/nuxt.js","npm_package_contributors_1_name":"Casian Blananru","npm_package_browserslist_1":"last 2 versions","npm_config_version_git_message":"v%s","npm_lifecycle_event":"nuxt:build","npm_package_scripts_isd_static_files_copy_local_custom_icon_definitions":"\\cp assets/custom-icons/*.svg ../packages/isd-sitepackage/Resources/Public/Icons/CustomIcons","npm_package_browserslist_2":"not ie <= 8","npm_package_repository_url":"git@git.p2media.de:p2media/internal/frontend/p2media-webpack-core.git","npm_package_version":"1.0.1","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"nuxt:build\"],\"original\":[\"run\",\"nuxt:build\"]}","npm_package_dependencies_nuxt_typo3_headless_news":"^0.1.0","npm_package_scripts_isd_static_files_copy_docker_rte_css":"mkdir -p ./dist/isd-sitepackage/Resources/Public/Frontend && \\cp assets/webpack-dist/* ./dist/isd-sitepackage/Resources/Public/Frontend","npm_package_dependencies_lodash":"^4.17.21","npm_package_dependencies_aos":"^2.3.4","npm_package_dependencies__fortawesome_pro_regular_svg_icons":"^5.15.1","npm_package_dependencies__fortawesome_free_brands_svg_icons":"^5.15.1","npm_package_devDependencies_sass_loader":"^10.1.1","npm_package_devDependencies__nuxt_image":"^0.7.1","npm_config_version_git_tag":"true","npm_config_version_git_sign":"","npm_package_dependencies_patch_package":"^6.2.2","npm_package_dependencies__labor_digital_vuetiful":"^1.36.4","npm_package_scripts_nuxt_start":"nuxt start","npm_package_license":"UNLICENSED","npm_config_strict_ssl":"true","npm_package_dependencies_js_cookie":"^3.0.5","npm_package_scripts_isd_static_files_create_rte_css":"webpack --config ./static-typo3/webpack.js","PWD":"/usr/src/app","npm_execpath":"/opt/yarn-v1.22.19/bin/yarn.js","npm_package_dependencies_vue_googleapis":"^1.0.10","npm_package_dependencies_pure":"^2.85.0","npm_package_dependencies_bootstrap":"^5.1.3","npm_package_scripts_isd_static_files_copy_custom_icon_definitions":"node util/is-local.js && yarn run isd:static-files:copy:local:custom-icon-definitions || yarn run isd:static-files:copy:docker:custom-icon-definitions","npm_package_dependencies_nuxt_typo3":"0.9.5","npm_package_author_name":"p2media","npm_package_dependencies_vue_removed_hook_mixin":"^0.1.2","npm_package_dependencies_bootstrap_vue":"^2.16.0","npm_config_save_prefix":"^","npm_config_ignore_optional":"","npm_package_devDependencies_nuxt_lazysizes":"^1.4.2","npm_package_dependencies_vue_select":"^3.10.8","npm_package_dependencies_vue_formulate_datepicker":"^0.1.14","npm_package_dependencies__nuxtjs_robots":"^2.5.0","npm_package_dependencies__nuxtjs_axios":"^5.13.6","npm_package_scripts_isd_static_files_copy_local_rte_css":"\\cp assets/webpack-dist/* ../packages/isd-sitepackage/Resources/Public/Frontend","npm_package_dependencies_vue_in_viewport_mixin":"^2.0.1","npm_package_dependencies_object_fit_images":"^3.2.4","npm_package_dependencies__fortawesome_fontawesome_svg_core":"^1.2.30","INIT_CWD":"/usr/src/app","npm_package_dependencies_nuxt_i18n":"^6.27.3","npm_config__fortawesome_registry":"https://npm.fontawesome.com/","NODE_ENV":"production","_applied":"true","VUE_ENV":"server","_AXIOS_BASE_URL_":"http://localhost:3000/"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
